import { usePermissions } from 'v2/shared/hooks/use-permissions'
import { useUserPlan } from 'v2/shared/hooks/use-plan'
import { canViewBuildSection, canViewGeoPermissions } from 'v2/shared/utils/role-based-visibility'
import { UserRole } from 'v2/types/api/auth'
import { RbacModules, RbacPermissionTypes } from 'v2/types/rbac'
import { getSessionRoles, sessionHasRole } from 'v2/utils'

export interface SettingsAccess {
  isAdmin: boolean
  canAccessUsers: boolean
  canAccessBilling: boolean
  canAccessEndpoints: boolean
  canAccessGeoPermissions: boolean
}

// RBAC checks shared by the user avatar popover (which shallow items to show)
// and the Organization settings page (which of its tabs to show) — kept in
// one hook so the two can't drift apart. See user-avatar-popover-sections.ts
// for the pure, unit-tested visibility computation that consumes these.
export const useSettingsAccess = (): SettingsAccess => {
  const { hasPermission } = usePermissions()
  const { isFreePlan } = useUserPlan()
  const isAdmin = sessionHasRole(UserRole.administrator)

  // Users: not free plan AND user.view permission. Teams no longer gates an
  // Org-settings tab — it moved to its own /teams page (gated on team.view),
  // reachable from the Human Specialist popover (SER-5859 follow-up).
  const canAccessUsers = !isFreePlan && hasPermission(RbacModules.user, RbacPermissionTypes.view)

  // Billing: permission + (admin OR finance_analyst)
  const canAccessBilling =
    hasPermission(RbacModules.billing, RbacPermissionTypes.view) &&
    (isAdmin || sessionHasRole(UserRole.finance_analyst))

  // Endpoints: reuse the exact route gate for /endpoints (canViewBuildSection) instead of
  // reimplementing it, so the popover item can never drift from the route's notAllowed check.
  const canAccessEndpoints = canViewBuildSection({ roles: getSessionRoles() })
  const canAccessGeoPermissions = canViewGeoPermissions({ roles: getSessionRoles() })

  return { isAdmin, canAccessUsers, canAccessBilling, canAccessEndpoints, canAccessGeoPermissions }
}
